import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Modal } from "antd";
import "./Modal.css";

type PopupModalProps = {
  modalTitle: string;
  children: React.ReactNode;
  wrapperClass?: string;
  hideDefaultButtons?: boolean;
  afterClose?: () => void;
};

/**
 * Reference interface for PopupModal component
 *
 * Provides imperative methods to control modal visibility programmatically
 */
export interface PopupModalRef {
  /** Opens the modal */
  openModal: () => void;
  /** Closes the modal */
  closeModal: () => void;
}

/**
 * PopupModal component
 *
 * A customizable modal component built on top of Ant Design's Modal.
 * Provides a reusable popup interface with configurable content and styling.
 *
 * Features:
 * - Imperative control via ref (open/close methods)
 * - Customizable title and content
 * - Optional default button hiding
 * - Custom CSS class support
 * - Accessibility features with proper ARIA labels
 * - Built-in close functionality (X button, ESC key, backdrop click)
 *
 * @param {PopupModalProps} props - Component props
 * @param {string} props.modalTitle - Title displayed in the modal header
 * @param {React.ReactNode} props.children - Content to display inside the modal
 * @param {string} [props.wrapperClass=""] - Additional CSS classes for modal styling
 * @param {boolean} [props.hideDefaultButtons=true] - Whether to hide Ant Design's default footer buttons
 *
 * @example
 * ```tsx
 * const modalRef = useRef<PopupModalRef>(null);
 *
 * const handleOpenModal = () => {
 *   modalRef.current?.openModal();
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleOpenModal}>Open Modal</Button>
 *     <PopupModal
 *       ref={modalRef}
 *       modalTitle="Create New Item"
 *       wrapperClass="custom-modal-style"
 *     >
 *       <CreateItemForm />
 *     </PopupModal>
 *   </>
 * );
 * ```
 *
 * @returns {JSX.Element} Modal component with configurable content
 */
const PopupModal = forwardRef<PopupModalRef, PopupModalProps>(
  (
    {
      modalTitle,
      children,
      wrapperClass = "",
      hideDefaultButtons = true,
      afterClose,
    },
    ref
  ) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = () => {
      setIsModalOpen(true);
    };

    const closeModal = () => {
      setIsModalOpen(false);
    };

    useImperativeHandle(ref, () => ({
      openModal,
      closeModal,
    }));

    return (
      <Modal
        title={modalTitle}
        closable={{ "aria-label": "Modal Close Button" }}
        open={isModalOpen}
        onCancel={closeModal}
        afterClose={afterClose}
        className={`default-popup-wrap ${wrapperClass}`}
        footer={hideDefaultButtons ? null : undefined}
      >
        {children}
      </Modal>
    );
  }
);

PopupModal.displayName = "PopupModal";

export default PopupModal;
